import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { can } from '../../utils/permissions';
import { errorText } from '../../utils/errorText';
import { badge, box, button, input, label } from '../shared/styles';

/*
 * A report on one analysis. It starts as the lab server's DRAFT (every number
 * in it audited against the results there); people edit it with a reason,
 * and someone other than the person who asked for it releases it.
 */
export default function ReportView({ experimentId, reportId }) {
  const { t } = useTranslation('experiments');
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [conclusions, setConclusions] = useState('');
  const [reason, setReason] = useState('');
  const [attest, setAttest] = useState(false);
  const [error, setError] = useState('');
  const path = `/experiments/${experimentId}/reports/${reportId}`;

  const take = (data) => { setReport(data); setConclusions(data.conclusions || ''); setReason(''); setAttest(false); };
  useEffect(() => {
    let alive = true;
    api.get(path).then(({ data }) => { if (alive) take(data); }).catch((e) => { if (alive) setError(errorText(e)); });
    return () => { alive = false; };
  }, [path]);

  const act = async (fn) => {
    setError('');
    try {
      take((await fn()).data);
    } catch (e) {
      setError(errorText(e));
    }
  };

  if (!report) return error ? <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p> : <p>{t('loading')}</p>;
  const draft = report.status === 'draft';
  const filled = report.conclusions !== null;
  const mine = report.requested_by === user?.user_id;
  const canEdit = draft && filled && can(user, 'experiment:write');
  const canRelease = draft && filled && !mine && can(user, 'research:approve');

  return (
    <section style={box} data-testid="report">
      <h4 style={{ marginTop: 0 }}>
        {t('report')} <span style={badge(draft ? 'warning' : report.status === 'released' ? 'success' : 'danger')}
          data-testid="report-status">{t(`status_${report.status}`)}</span>
      </h4>
      {!filled && <p data-testid="report-waiting">{t('reportWaiting')}</p>}
      {filled && (
        <>
          <p style={{ fontSize: 12, color: 'var(--c-text-muted-fg)' }} data-testid="ai-disclosure">{report.ai_disclosure}</p>
          {report.dropped_statements?.length > 0 && (
            <p style={{ fontSize: 13, color: 'var(--c-warning-fg)' }} data-testid="dropped">
              {t('droppedStatements', { count: report.dropped_statements.length })}
            </p>
          )}
          <label style={label}>{t('conclusions')}
            <textarea style={input} rows={5} value={conclusions} readOnly={!canEdit}
              onChange={(e) => setConclusions(e.target.value)} title={t('tip.conclusions')} data-testid="conclusions" />
          </label>
          <h5>{t('findings')}</h5>
          <ul>{report.findings.map((f, i) => (
            <li key={i}><strong>{f.outcome}</strong>: {f.statement}
              {f.significant !== null && <span style={badge(f.significant ? 'success' : 'info')}>
                {f.significant ? t('significant') : t('notDetected')}</span>}
            </li>))}</ul>
          <h5>{t('limitations')}</h5>
          <ul>{report.limitations.map((l, i) => <li key={i}>{l}</li>)}</ul>
          <h5>{t('followUps')}</h5>
          <ul>{report.follow_ups.map((f, i) => (
            <li key={i}><span style={badge('purple')}>{t(`followUp_${f.kind}`)}</span> {f.statement}
              {f.rationale && <small style={{ color: 'var(--c-text-muted-fg)' }}> — {f.rationale}</small>}</li>
          ))}</ul>
        </>
      )}
      {report.status === 'released' && (
        <p data-testid="released-note">{t('releasedNote', { reason: report.release_reason })}</p>
      )}
      {(canEdit || canRelease || report.status !== 'withdrawn') && (
        <label style={label}>{t('reason')}
          <input style={input} value={reason} onChange={(e) => setReason(e.target.value)} title={t('tip.reportReason')} data-testid="report-reason" />
        </label>
      )}
      {canEdit && (
        <button type="button" onClick={() => act(() => api.put(path, { conclusions, reason }))} title={t('tip.saveReport')} data-testid="save-report">
          {t('saveReport')}
        </button>
      )}
      {canRelease && (
        <div style={{ marginTop: 8 }}>
          <label><input type="checkbox" checked={attest} onChange={(e) => setAttest(e.target.checked)}
            title={t('tip.releaseAttest')} data-testid="release-attest" /> {t('releaseAttest')}</label>
          <div>
            <button type="button" style={button('var(--c-primary)')} data-testid="release-report" title={t('tip.release')}
              onClick={() => act(() => api.post(`${path}/release`, { reason, attest }))}>{t('release')}</button>
          </div>
        </div>
      )}
      {draft && filled && mine && <p style={{ fontSize: 13 }}>{t('secondPerson')}</p>}
      {report.status !== 'withdrawn' && (mine || can(user, 'research:approve')) && (
        <button type="button" onClick={() => act(() => api.post(`${path}/withdraw`, { reason }))} title={t('tip.withdraw')} data-testid="withdraw-report">
          {t('withdraw')}
        </button>
      )}
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
    </section>
  );
}
